import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CreditCard,
  Key,
  Shield,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Trash2,
  ExternalLink,
  Zap,
  Lock
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DashboardLayout from "@/components/DashboardLayout";

import { API_URL } from "@/utils/api";

export default function StripeIntegrationPage({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [removing, setRemoving] = useState(false);
  
  // Form state
  const [secretKey, setSecretKey] = useState("");
  const [publishableKey, setPublishableKey] = useState("");
  const [showSecretKey, setShowSecretKey] = useState(false);

  const isPowerUser = user?.plan === "power";

  useEffect(() => {
    fetchStripeStatus();
  }, []);

  const fetchStripeStatus = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/user/stripe-keys`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setStripeStatus(data);
      }
    } catch (err) {
      console.error("Failed to fetch Stripe status:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKeys = async (e) => {
    e.preventDefault();
    
    if (!secretKey) {
      toast.error("Secret key is required");
      return;
    }
    
    if (!secretKey.startsWith("sk_live_") && !secretKey.startsWith("sk_test_")) {
      toast.error("Invalid secret key format. Must start with sk_live_ or sk_test_");
      return;
    }
    
    if (publishableKey && !publishableKey.startsWith("pk_live_") && !publishableKey.startsWith("pk_test_")) {
      toast.error("Invalid publishable key format. Must start with pk_live_ or pk_test_");
      return;
    }

    setSaving(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/user/stripe-keys`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          stripe_api_key: secretKey,
          stripe_publishable_key: publishableKey || null
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success("Stripe keys saved successfully!");
        setSecretKey("");
        setPublishableKey("");
        fetchStripeStatus();
      } else {
        toast.error(data.detail || "Failed to save Stripe keys");
      }
    } catch (err) {
      toast.error("Failed to save Stripe keys");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveKeys = async () => {
    setRemoving(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/user/stripe-keys`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ remove_keys: true })
      });
      
      if (res.ok) {
        toast.success("Stripe keys removed. Now using Revurge's Stripe.");
        fetchStripeStatus();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to remove keys");
      }
    } catch (err) {
      toast.error("Failed to remove keys");
    } finally {
      setRemoving(false);
      setShowRemoveDialog(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#BFFF00]" />
        </div>
      </DashboardLayout>
    );
  }

  // Not a Power user
  if (!isPowerUser) {
    return (
      <DashboardLayout user={user}>
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-dashed border-purple-300 bg-purple-50 rounded-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Power User Feature</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Custom Stripe integration is exclusively available for Power users. 
                Upgrade your plan to use your own Stripe account and bypass platform fees.
              </p>
              <div className="flex items-center justify-center gap-4">
                <Badge className="bg-purple-100 text-purple-700 px-3 py-1">
                  Current Plan: {user?.plan || "Free"}
                </Badge>
                <Button 
                  className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full"
                  onClick={() => window.location.href = "/dashboard/plans"}
                >
                  Upgrade to Power
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Manrope']">Stripe Integration</h1>
          <p className="text-gray-500 mt-1">Connect your own Stripe account to process payments directly</p>
        </div>

        {/* Current Status Card */}
        <Card className={`border-2 rounded-2xl ${stripeStatus?.has_custom_stripe ? 'border-green-300 bg-green-50' : 'border-[#BFFF00]/30 bg-[#F7FFE5]'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${stripeStatus?.has_custom_stripe ? 'bg-green-100' : 'bg-[#BFFF00]/30'}`}>
                {stripeStatus?.has_custom_stripe ? (
                  <CheckCircle className="w-7 h-7 text-green-600" />
                ) : (
                  <CreditCard className="w-7 h-7 text-[#121212]" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">
                  {stripeStatus?.has_custom_stripe ? "Using Your Own Stripe" : "Using Revurge's Stripe"}
                </h3>
                <p className="text-sm text-gray-600">
                  {stripeStatus?.has_custom_stripe 
                    ? "Payments are processed through your Stripe account with 0% platform fee"
                    : "Payments are processed through Revurge with standard platform fees"}
                </p>
              </div>
              <Badge className={`rounded-full px-3 py-1 ${stripeStatus?.has_custom_stripe ? 'bg-green-100 text-green-700' : 'bg-[#BFFF00]/30 text-[#121212]'}`}>
                {stripeStatus?.has_custom_stripe ? "0% Fee" : "1% Fee"}
              </Badge>
            </div>

            {stripeStatus?.has_custom_stripe && stripeStatus?.stripe_publishable_key && (
              <div className="mt-4 p-3 bg-white rounded-xl border border-green-200">
                <p className="text-xs text-gray-500 mb-1">Publishable Key</p>
                <code className="text-sm text-gray-700 font-mono">
                  {stripeStatus.stripe_publishable_key.slice(0, 12)}...{stripeStatus.stripe_publishable_key.slice(-4)}
                </code>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Benefits Card */}
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-bold font-['Manrope'] flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#BFFF00]" />
              Benefits of Your Own Stripe
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">0% Platform Fee</p>
                  <p className="text-sm text-gray-500">No Revurge fees on transactions</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Direct Payouts</p>
                  <p className="text-sm text-gray-500">Funds go directly to your bank</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Full Control</p>
                  <p className="text-sm text-gray-500">Manage disputes, refunds directly</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Key className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Your Branding</p>
                  <p className="text-sm text-gray-500">Customers see your business name</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add/Update Keys Form */}
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-bold font-['Manrope'] flex items-center gap-2">
              <Key className="w-5 h-5 text-gray-700" />
              {stripeStatus?.has_custom_stripe ? "Update Stripe Keys" : "Connect Your Stripe"}
            </CardTitle>
            <CardDescription>
              Enter your Stripe API keys from your{" "}
              <a 
                href="https://dashboard.stripe.com/apikeys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                Stripe Dashboard <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveKeys} className="space-y-4">
              <div>
                <Label className="text-gray-700">Secret Key (Required)</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showSecretKey ? "text" : "password"}
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder="sk_live_..."
                    className="pr-10 rounded-xl border-gray-200 font-mono text-sm"
                    data-testid="stripe-secret-key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Starts with sk_live_ or sk_test_</p>
              </div>
              
              <div>
                <Label className="text-gray-700">Publishable Key (Optional)</Label>
                <Input
                  type="text"
                  value={publishableKey}
                  onChange={(e) => setPublishableKey(e.target.value)}
                  placeholder="pk_live_..."
                  className="mt-1.5 rounded-xl border-gray-200 font-mono text-sm"
                  data-testid="stripe-publishable-key"
                />
                <p className="text-xs text-gray-500 mt-1">Starts with pk_live_ or pk_test_</p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={saving || !secretKey}
                  className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full"
                  data-testid="save-stripe-keys-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Validating...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      {stripeStatus?.has_custom_stripe ? "Update Keys" : "Save Keys"}
                    </>
                  )}
                </Button>
                
                {stripeStatus?.has_custom_stripe && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowRemoveDialog(true)}
                    className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="remove-stripe-keys-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Keys
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <Card className="border border-amber-200 bg-amber-50 rounded-2xl">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-amber-900">Security Notice</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Your Stripe secret key is securely stored and encrypted. We never share your keys 
                  with third parties. We recommend using restricted API keys with only the necessary 
                  permissions for added security.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Remove Keys Confirmation Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Your Stripe Keys?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove your custom Stripe integration. All future payments will be 
              processed through Revurge's Stripe with standard platform fees (1%).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveKeys}
              disabled={removing}
              className="bg-red-600 hover:bg-red-700 rounded-full"
              data-testid="confirm-remove-keys-btn"
            >
              {removing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Removing...
                </>
              ) : (
                "Remove Keys"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
